import React, { Component, Context } from "react";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import scrollIntoView from "scroll-into-view-if-needed";
import PreviewIcon from "@material-ui/icons/VisibilityOutlined";
import { DynamicFormFieldProps, DynamicForm } from "../DynamicForm";
import { FormLabel } from "../InputComponents";
import { DynamicFormField, DynamicFormValidation } from "../../laco-common/base/form-validation/dynamic";
import { IFormField, ISelectFormField } from "../../laco-common";
import { getDynamicFormFromConfig, getValue } from "../../laco-common/base/form-validation/utils";
import { WithOptional } from "../../laco-common/helpers/extendedTypes";
import { UIActions } from "../../components/peer-support/Actions";
import { waitForElement, waitForElements } from "../../utils";
import { asyncForEach } from "../../laco-common/base/utils";
import UILoader from "../../components/ui/UILoader";
import AppContext, { IAppContext } from "../../contexts/app/AppContext";
import NotificationsService from "../../laco-common/services/notifications";
import { withAuth } from "../../laco-common/auth/AuthContext";
import { IAuthContext } from "../../laco-common/base/auth";
import "./ItemForm.scss";

class ItemFormInput<T> extends Component<
	DynamicFormFieldProps & {
		itemName: string;
		nameField: string;
		viewItemSelector: string;
		getFields: (
			data: T
		) => Record<keyof T, DynamicFormField & WithOptional<Partial<ISelectFormField>, "id" | "fieldType">>;
		createNew: (i?: number) => T;
		onFormChange?: (field: string, f: DynamicFormValidation, i: number) => any;
		containerClassName?: string;
		itemClassName?: string;
		authContext: IAuthContext;
	},
	{ loading: boolean; listItems: T[]; forms: DynamicFormValidation[]; refresh: number; animateIndex: number }
> {
	static contextType: Context<IAppContext> = AppContext;
	private readonly uuid = uuidv4();

	state = {
		loading: true,
		listItems: this.props.field.value || [],
		forms: [],
		refresh: 0,
		animateIndex: undefined,
	};

	async componentDidMount() {
		this.init();
	}

	async init(listItems: T[] = this.state.listItems) {
		console.log("ItemForm init(): listItems:", listItems);
		const forms: DynamicFormValidation[] = [];
		await asyncForEach(listItems, async (block: T) => {
			const form: DynamicFormValidation = await getDynamicFormFromConfig(
				this.props.getFields(block) as { [key: string]: IFormField },
				this.props.authContext,
				this.context.firebase,
				[],
				block
			);
			forms.push(form);
		});
		this.setState({ forms, listItems: listItems, loading: false });
	}

	async onAdd(i: number) {
		console.log("ItemForm.onAdd() index:", i);
		const newBlocksArr: T[] = [...this.state.listItems];
		const newBlock: T = this.props.createNew(i);
		newBlocksArr.splice(i + 1, 0, newBlock);
		console.log("newBlocksArr:", newBlocksArr);
		this.props.onChange(newBlocksArr);
		await this.init(newBlocksArr);
		this.setState({ animateIndex: i + 1, refresh: this.state.refresh + 1 });
		setTimeout(() => {
			waitForElement(
				`#items-form-container-${this.uuid}${
					this.props.containerClassName ? `.${this.props.containerClassName}` : ""
				} ${this.props.itemClassName ? `.${this.props.itemClassName}` : ""}.ui-list-item-form-${i + 1}`,
				(el) => {
					scrollIntoView(el, {
						scrollMode: "if-needed",
						block: "start",
						inline: "start",
					});
				}
			);
		}, 200);
		setTimeout(() => {
			this.setState({ animateIndex: undefined });
		}, 1200);
		return Promise.resolve();
	}

	async onDelete(block: T, i: number) {
		console.log("ItemForm.onDelete(): block:", block);
		const newBlocksArr: T[] = [...this.state.listItems];
		newBlocksArr.splice(i, 1);
		console.log("newBlocksArr:", newBlocksArr);
		this.props.onChange(newBlocksArr);
		await this.init(newBlocksArr);
		this.setState({ refresh: this.state.refresh + 1 });
		return Promise.resolve();
	}

	async onPreview(i: number): Promise<any> {
		console.log("onPreview:", i);
		document.getElementById("add-item-toggle-view").click();
		setTimeout(() => {
			// waitForElement(`.ui-text-block:nth-of-type(${i + 1})`, (el) => {
			waitForElements(this.props.viewItemSelector, (arr: HTMLElement[]) => {
				if (arr[i])
					scrollIntoView(arr[i], {
						scrollMode: "if-needed",
						block: "start",
						inline: "start",
					});
			});
		}, 200);
		return Promise.resolve();
	}

	onFormChange(field: string, f: DynamicFormValidation, i: number) {
		console.log("onFormChange:", f, i);
		if (!f.validate()) {
			NotificationsService.error("Fix form errors");
			return;
		}
		if (this.props.onFormChange) return this.props.onFormChange(field, f, i);
		const updatedBlocksArr: T[] = [...this.state.listItems];
		updatedBlocksArr[i][field] = f.fields[field].value;
		console.log("updatedBlocksArr:", updatedBlocksArr);
		this.props.onChange(updatedBlocksArr);
	}

	render() {
		const { omitLabel, field } = this.props;

		return (
			<div className="w-100 position-relative" key={this.state.refresh}>
				{!omitLabel && <FormLabel field={field} />}
				{this.state.loading && <UILoader display={true} />}
				{!this.state.loading && !this.state.listItems?.length && (
					<UIActions
						itemName={this.props.nameField}
						onAdd={() => this.onAdd(-1)}
						addName={this.props.itemName}
						isSimpleDelete={true}
						className="empty-add-new"
					/>
				)}
				{!this.state.loading && this.state.listItems?.length > 0 && (
					<div
						id={`items-form-container-${this.uuid}`}
						className={clsx(
							"d-flex flex-column flex-md-row align-items-start flex-wrap items-form-container",
							this.props.containerClassName
						)}
					>
						{(this.state.listItems || []).map((block: T, i: number) => {
							const form: DynamicFormValidation = this.state.forms[i];
							return (
								<div
									className={clsx(
										"ui-list-item-form float-left1",
										`ui-list-item-form-${i}`,
										this.state.animateIndex === i && "animated pulse",
										this.props.itemClassName
									)}
									key={this.state.refresh + i}
								>
									<span
										className="badge badge-secondary position-absolute"
										style={{ left: "5px", top: "5px" }}
									>
										{i + 1}
									</span>
									<UIActions
										data={block}
										itemName={
											block && this.props.nameField
												? block[this.props.nameField]
												: this.props.itemName
										}
										onAdd={() => this.onAdd(i)}
										customActions={[
											{ title: "Preview", callback: () => this.onPreview(i), icon: PreviewIcon },
										]}
										addName={this.props.itemName}
										onDelete={(block: T) => this.onDelete(block, i)}
										isSimpleDelete={true}
									/>
									<DynamicForm
										key={this.state.refresh}
										skipAutoFocus={this.state.animateIndex !== i}
										form={form}
										noFooter={true}
										onChange={(field: string, f: DynamicFormValidation) =>
											this.onFormChange(field, f, i)
										}
									/>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	}
}

export default withAuth(ItemFormInput, true);
